// src/components/Header.js
"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import "./header.css";
import NextImage from "next/image";


const DEFAULT_API_CATEGORIES = {
  services: [
    {
      name: "Design & Development",
      children: [
        { name: "Website Development", slug: "website-development" },
        { name: "Mobile App Development", slug: "mobile-app-development" },
        { name: "Ecommerce Development", slug: "ecommerce-development" },
        { name: "Software Development", slug: "software-development" },
        { name: "Game Development", slug: "game-development" }
      ]
    },
    {
      name: "Enterprise Services",
      children: [
        { name: "CRM Solutions", slug: "crm-solutions" },
        { name: "ERP Next", slug: "erp-next" },
        { name: "Custom Software Integration", slug: "custom-software-integration" },
        { name: "DMS Document Management System", slug: "dmsdocument-management-system" },
        { name: "Cloud Solutions", slug: "cloud-solutions" }
      ]
    },
    {
      name: "AI Agents",
      children: [
        { name: "AI Call Agent", slug: "ai-call-agent" },
        { name: "AI Chat Bot", slug: "ai-chat-bot" },
        { name: "AI / ML", slug: "aiml" }
      ]
    },
    {
      name: "Support Services",
      children: [
        { name: "IT Support Services", slug: "it-support-services" },
        { name: "Server Support", slug: "server-support" },
        { name: "Hosting", slug: "hosting" },
        { name: "DevOps", slug: "devops" },
        { name: "Development Support", slug: "development-support" }
      ]
    },
    {
      name: "Digital Marketing",
      children: [
        { name: "Search Engine Optimization", slug: "search-engine-optimization" },
        { name: "Pay Per Click", slug: "pay-per-click" },
        { name: "Social Media Marketing", slug: "social-media-marketing" },
        { name: "ORM", slug: "orm" },
        { name: "Performance Optimization", slug: "performance-optimization" }
      ]
    }
  ],
  technologies: [
    {
      name: "Mobile Apps",
      slug: "mobile-apps",
      children: [
        { name: "Android", slug: "android" },
        { name: "React Native", slug: "react-native" },
        { name: "iOS", slug: "ios" },
        { name: "Flutter", slug: "flutter" }
      ]
    },
    {
      name: "Ecommerce",
      slug: "ecommerce",
      children: [
        { name: "Shopify", slug: "shopify" },
        { name: "WooCommerce", slug: "woocommerce" },
        { name: "Magento", slug: "magento" }
      ]
    },
    {
      name: "CMS",
      slug: "cms",
      children: [
        { name: "WordPress", slug: "wordpress" },
        { name: "Drupal", slug: "drupal" },
        { name: "Joomla", slug: "joomla" }
      ]
    },
    {
      name: "Full Stack Development",
      slug: "full-stack-development",
      children: [
        { name: "MERN", slug: "mern" },
        { name: "MEAN", slug: "mean" },
        { name: "Java", slug: "java" }
      ]
    },
    {
      name: "Custom Development",
      slug: "custom-development",
      children: [
        { name: "PHP", slug: "php" },
        { name: "Node.js", slug: "nodejs" },
        { name: "Python", slug: "python" },
        { name: "React JS", slug: "react-js" }
      ]
    }
  ]
};

function Header() {
  const pathname = usePathname();

  const [openMega, setOpenMega] = useState({
    services: false,
    technologies: false,
  });

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [apiCategories, setApiCategories] = useState(DEFAULT_API_CATEGORIES);
  const [isNavOpen, setIsNavOpen] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
        const res = await fetch(`${apiUrl}/api/category/active`);
        if (!res.ok) {
          console.warn(`Header: Categories API responded with status ${res.status}. Using default navigation.`);
          setApiCategories(DEFAULT_API_CATEGORIES);
          return;
        }
        const data = await res.json();
        if (data.success && Array.isArray(data.data) && data.data.length > 0) {
          const all = data.data;
          const getChildren = (parentName) => {
            const parent = all.find(c => c.name === parentName);
            if (!parent) return [];
            const subs = all.filter(c => c.parentId && c.parentId._id === parent._id);
            const result = [];
            subs.forEach(sub => {
              const children = all.filter(c => c.parentId && c.parentId._id === sub._id);
              result.push({ ...sub, children });
            });
            return result;
          };
          const services = getChildren('Service');
          const technologies = getChildren('Technologies');
          if (services.length > 0 || technologies.length > 0) {
            setApiCategories({
              services: services.length > 0 ? services : DEFAULT_API_CATEGORIES.services,
              technologies: technologies.length > 0 ? technologies : DEFAULT_API_CATEGORIES.technologies
            });
            return;
          }
        }
        setApiCategories(DEFAULT_API_CATEGORIES);
      } catch (err) {
        console.warn("Header: API server unreachable or error fetching categories. Using default navigation.");
        setApiCategories(DEFAULT_API_CATEGORIES);
      }
    };

    fetchCategories();
  }, []);

  const iconMapping = {
    "Design & Development": "fa-laptop-code",
    "Enterprise Services": "fa-gear",
    "AI Agents": "fa-robot",
    "Support Services": "fa-headset",
    "Digital Marketing": "fa-bullhorn",
    "Mobile Apps": "fa-mobile-screen-button",
    "Full Stack  Development": "fa-layer-group",
    "CMS": "fa-newspaper",
    "Custom Development": "fa-code",
    "Ecommerce": "fa-cart-shopping",
    "CRM": "fa-briefcase",
    "Games": "fa-gamepad",
    "Cloud": "fa-cloud",
    "Other": "fa-shuffle"
  };

  const isMobile = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 991.98px)").matches;

  const handleNavLinkClick = () => {
    setIsNavOpen(false);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isNavOpen]);

  const handleMegaLinkClick = () => {
    const servicesTrigger = document.querySelector(".mega-parent > .nav-link");
    if (servicesTrigger && typeof servicesTrigger.blur === "function") {
      servicesTrigger.blur();
    }
    document.documentElement.classList.add("mega-force-hide");
    setTimeout(() => {
      handleNavLinkClick();
    }, 220);
  };

  useEffect(() => {
    const root = document.documentElement;
    if (root.classList.contains("mega-force-hide")) {
      setTimeout(() => root.classList.remove("mega-force-hide"), 240);
    }
  }, [pathname]);

  useEffect(() => {
    const handleDocClick = (e) => {
      if (!isMobile()) return;
      const parents = document.querySelectorAll(".mega-parent");
      let inside = false;
      parents.forEach((p) => {
        if (p.contains(e.target)) inside = true;
      });
      if (!inside) setOpenMega({ services: false, technologies: false });
    };
    const handleKey = (e) => {
      if (!isMobile()) return;
      if (e.key === "Escape")
        setOpenMega({ services: false, technologies: false });
    };
    document.addEventListener("click", handleDocClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("click", handleDocClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = document.documentElement;
    const headerEl = document.querySelector(".navbarcolor");
    if (!headerEl) return;

    let ro;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const h = Math.ceil(
            entry.borderBoxSize?.[0]?.blockSize || entry.contentRect?.height || 0
          );
          if (h > 0) root.style.setProperty("--header-h", `${h}px`);
        }
      });
      ro.observe(headerEl);
    } else {
      const setVar = () => {
        window.requestAnimationFrame(() => {
          const h = Math.ceil(headerEl.offsetHeight || 0);
          if (h > 0) root.style.setProperty("--header-h", `${h}px`);
        });
      };
      setVar();
      window.addEventListener("resize", setVar, { passive: true });
      return () => window.removeEventListener("resize", setVar);
    }

    return () => {
      if (ro) ro.disconnect();
    };
  }, []);

  const toggleMega = (key) => (e) => {
    if (!isMobile()) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    setOpenMega((prev) => {
      const next = { services: false, technologies: false };
      next[key] = !prev[key];
      return next;
    });
    const el = e.currentTarget;
    if (el && typeof el.blur === "function") el.blur();
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = [];
    const lowerQuery = query.toLowerCase();

    apiCategories.services.forEach(parent => {
      parent.children.forEach(child => {
        if (child.name.toLowerCase().includes(lowerQuery) || parent.name.toLowerCase().includes(lowerQuery)) {
          results.push({
            name: child.name,
            category: parent.name,
            url: `/services/${child.slug}`,
            type: 'Service'
          });
        }
      });
    });

    apiCategories.technologies.forEach(parent => {
      parent.children.forEach(child => {
        if (child.name.toLowerCase().includes(lowerQuery) || parent.name.toLowerCase().includes(lowerQuery)) {
          results.push({
            name: child.name,
            category: parent.name,
            url: `/${parent.slug}/${child.slug}`,
            type: 'Technology'
          });
        }
      });
    });

    setSearchResults(results.slice(0, 8));
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  return (
    <>
      <div className="navbarcolor">
        <nav className="navbar navbar-expand-lg navbar-dark">
          <div className="container">
            <Link href="/" className="navbar-brand">
              <NextImage
                src="/assets/img/CodeQlik_logo.png"
                alt="CodeQlik logo"
                width={103}
                height={30}
                priority
                style={{ height: "30px", width: "103px" }}
                className="d-inline-block align-text-top logo-img"
              />
            </Link>

            <button
              className="navbar-toggler"
              type="button"
              onClick={() => setIsNavOpen(!isNavOpen)}
              aria-controls="navbarNav"
              aria-expanded={isNavOpen}
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            <div className={`collapse navbar-collapse header-code ${isNavOpen ? "show" : ""}`} id="navbarNav">
              <button 
                className="menu-close-btn d-lg-none"
                onClick={handleNavLinkClick}
                aria-label="Close Menu"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>

              {/* Mobile-only Search Bar at the top */}
              <div className="d-block d-lg-none mobile-search-container mt-4 mb-2">
                <div className="search-pill-container-inline w-100">
                  <i className="fa-solid fa-magnifying-glass text-white me-2"></i>
                  <input
                    type="text"
                    placeholder="Search..."
                    className="search-pill-input-inline"
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  {searchQuery && (
                    <button className="search-pill-clear-inline" onClick={closeSearch}>
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <div className="search-pill-dropdown-inline mt-2">
                    {searchResults.length > 0 ? (
                      searchResults.map((res, i) => (
                        <Link key={i} href={res.url} className="search-pill-item-inline" onClick={closeSearch}>
                          <span className="pill-item-name-inline">{res.name}</span>
                        </Link>
                      ))
                    ) : (
                      <div className="no-results-inline">No results found</div>
                    )}
                  </div>
                )}
              </div>

              <ul className="navbar-nav mx-auto">
                <li className="nav-item m-2">
                  <Link href="/" className={`nav-link ${pathname === "/" ? "active" : ""}`} onClick={handleNavLinkClick}>
                    Home
                  </Link>
                </li>

                <li className="nav-item m-2">
                  <Link href="/about-us" className={`nav-link ${pathname === "/about-us" ? "active" : ""}`} onClick={handleNavLinkClick}>
                    About Us
                  </Link>
                </li>

                {/* Services Mega Menu */}
                <li className={`nav-item m-2 mega-parent ${openMega.services ? "open" : ""}`}
                  onClick={(e) => {
                    if (!isMobile()) return;
                    const mm = e.currentTarget.querySelector(".mega-menu");
                    if (mm && mm.contains(e.target)) return;
                    toggleMega("services")(e);
                  }}
                >
                  <Link href="#" className={`nav-link ${pathname.startsWith("/services") ? "active" : ""}`}
                    onClick={(e) => {
                      if (!isMobile()) return;
                      e.preventDefault();
                      e.stopPropagation();
                      setOpenMega((prev) => ({ services: !prev.services, technologies: false }));
                    }}
                  >
                    <span>Services</span>
                    <i className={`fa-solid ${openMega.services ? "fa-chevron-up" : "fa-chevron-down"} dropdown-caret`}></i>
                  </Link>
                  <div className="mega-menu services-menu" suppressHydrationWarning>
                    <div className="container-fluid mega-container" suppressHydrationWarning>
                      <div className="row g-4 justify-content-center" suppressHydrationWarning>
                        {apiCategories.services.map((parent, idx) => (
                          <div key={idx} className="col-12 col-md-6 col-lg-2">
                            <div className="mega-title">
                              <i className={`fa-solid ${iconMapping[parent.name] || 'fa-circle'} mega-icon`}></i>
                              {parent.name}
                            </div>
                            <ul className={`mega-list ${parent.children.length > 8 ? 'two-col' : ''}`}>
                              {parent.children.map((child, cIdx) => (
                                <li key={cIdx}>
                                  <Link href={`/services/${child.slug}`} onClick={handleMegaLinkClick}>{child.name}</Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </li>
                


                {/* Technologies Mega Menu */}
                <li className={`nav-item m-2 mega-parent ${openMega.technologies ? "open" : ""}`}
                  onClick={(e) => {
                    if (!isMobile()) return;
                    const mm = e.currentTarget.querySelector(".mega-menu");
                    if (mm && mm.contains(e.target)) return;
                    toggleMega("technologies")(e);
                  }}
                >
                  <Link href="#" className={`nav-link ${apiCategories.technologies.some(p => pathname.startsWith(`/${p.slug}`)) ? "active" : ""}`}
                    onClick={(e) => {
                      if (!isMobile()) return;
                      e.preventDefault();
                      e.stopPropagation();
                      setOpenMega((prev) => ({ services: false, technologies: !prev.technologies }));
                    }}
                  >
                    <span>Technologies</span>
                    <i className={`fa-solid ${openMega.technologies ? "fa-chevron-up" : "fa-chevron-down"} dropdown-caret`}></i>
                  </Link>
                  <div className="mega-menu technologies-menu" suppressHydrationWarning>
                    <div className="container-fluid mega-container" suppressHydrationWarning>
                      <div className="row g-8 justify-content-center" suppressHydrationWarning>
                        {apiCategories.technologies.map((parent, idx) => (
                          <div key={idx} className="col-12 col-md-6 col-lg-2">
                            <div className="mega-title">
                              <i className={`fa-solid ${iconMapping[parent.name] || 'fa-circle'} mega-icon`}></i>
                              {parent.name}
                            </div>
                            <ul className={`mega-list ${parent.children.length > 8 ? 'two-col' : ''}`}>
                              {parent.children.map((child, cIdx) => (
                                <li key={cIdx}>
                                  <Link href={`/${parent.slug}/${child.slug}`} onClick={handleMegaLinkClick}>{child.name}</Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </li>

                <li className="nav-item m-2">
                  <Link href="/projects" className={`nav-link ${pathname.startsWith("/projects") ? "active" : ""}`} onClick={handleNavLinkClick}>Projects</Link>
                </li>
                <li className="nav-item m-2">
                  <Link href="/blog" className={`nav-link ${pathname === "/blog" ? "active" : ""}`} onClick={handleNavLinkClick}>Blogs</Link>
                </li>
                <li className="nav-item m-2">
                  <Link href="/staff-augmentation" className={`nav-link ${pathname === "/staff-augmentation" ? "active" : ""}`} onClick={handleNavLinkClick}>Staff Augmentation</Link>
                </li>
                <li className="nav-item m-2">
                  <Link href="/odoo" className={`nav-link odoo-nav-link ${pathname === "/odoo" ? "active" : ""}`} onClick={handleNavLinkClick}>
                    <NextImage
                      src="/assets/img/odoo_logo_white.svg"
                      alt="Odoo"
                      width={60}
                      height={20}
                      className="odoo-logo-img"
                    />
                  </Link>
                </li>
                <li className="nav-item m-2">
                  <Link href="/careers" className={`nav-link ${pathname === "/careers" ? "active" : ""}`} onClick={handleNavLinkClick}>Careers</Link>
                </li>
              </ul>

              <div className="d-lg-flex align-items-center ms-lg-2 mt-3 mt-lg-0 gap-2">
                <div className="search-pill-wrapper d-none d-lg-block" style={{ position: 'relative' }}>
                  <button className="search-icon-btn" onClick={() => setIsSearchOpen(!isSearchOpen)} aria-label="Search">
                    <i className="fa-solid fa-magnifying-glass"></i>
                  </button>

                  {isSearchOpen && (
                    <div className="search-floating-box">
                      <div className="search-pill-container-inline">
                        <input
                          type="text"
                          placeholder="Search..."
                          className="search-pill-input-inline"
                          value={searchQuery}
                          onChange={handleSearch}
                          autoFocus
                        />
                        <div className="search-pill-actions">
                          <button className="search-pill-clear-inline" onClick={closeSearch}>
                            <i className="fa-solid fa-xmark"></i>
                          </button>
                        </div>
                      </div>

                      {searchQuery && (
                        <div className="search-pill-dropdown-inline">
                          {searchResults.length > 0 ? (
                            searchResults.map((res, i) => (
                              <Link key={i} href={res.url} className="search-pill-item-inline" onClick={closeSearch}>
                                <span className="pill-item-name-inline">{res.name}</span>
                                {/* <span className="pill-item-cat-inline">{res.category}</span> */}
                              </Link>
                            ))
                          ) : (
                            <div className="no-results-inline">No results found</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Link href="/contact" className="contact-nav-btn" onClick={handleNavLinkClick}>Contact Us</Link>
              </div>
            </div>
          </div>
        </nav>
      </div>
      {isNavOpen && (
        <div className="nav-drawer-backdrop d-lg-none" onClick={() => setIsNavOpen(false)}></div>
      )}
    </>
  );
}

export default Header;